import React, { useEffect, useState } from 'react';
import styles from './AppointmentTypeStep.module.css';
import shared from './SchedulingFlow.module.css';
import telehealthIcon from './SVGs/Device-Imac--Streamline-Tabler.svg';
import inpersonIcon from './SVGs/Building-Community--Streamline-Tabler (1).svg';
import bgImg from './SVGs/image001.png';
import { AxisBrandLogo, axisBranding } from './branding';

interface AppointmentTypeStepProps {
  onSelect: (type: 'telehealth' | 'inperson') => void;
  onBack: () => void;
}

const AppointmentTypeStep: React.FC<AppointmentTypeStepProps> = React.memo(({ onSelect, onBack }) => {
  const [selected, setSelected] = useState<'telehealth' | 'inperson' | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  const checkMobile = React.useCallback(() => {
    setIsMobile(window.innerWidth <= 700);
  }, []);

  useEffect(() => {
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [checkMobile]);

  // Navigation links for mobile responsiveness
  const NAV_PHONE = axisBranding.phoneNumber;
  const NAV_LINKS = [
    { label: 'HOME', href: '#', action: 'home' },
  ];
  const NAV_LINKS_MOBILE = [
    { label: 'HOME', href: '#', action: 'home' },
  ];
  const currentNavLinks = isMobile ? NAV_LINKS_MOBILE : NAV_LINKS;

  const handleNavClick = (action: string) => {
    if (action === 'home') {
      // Refresh the page to go back to landing
      window.location.reload();
    }
  };

  return (
    <div className={shared.landingRoot}>
      <div className={shared.topBanner}>
        <img src={bgImg} alt="Banner" className={shared.bannerImg} />
        <div className={shared.bannerOverlay} />
        <div className={shared.navBar}>
          <AxisBrandLogo
            variant="white"
            size="small"
            className={shared.navLogo}
            onClick={() => window.open(axisBranding.websiteUrl, '_blank')}
            style={{ cursor: 'pointer' }}
          />
          <span className={shared.navPhone}>{NAV_PHONE}</span>
          <div className={shared.navLinks}>
            {currentNavLinks.map(link => (
              <button key={link.label} className={shared.navLink} onClick={() => handleNavClick(link.action)}>{link.label}</button>
            ))}
          </div>
        </div>
        <div className={shared.bannerContent}>
          <h1 className={shared.bannerTitle}>Book Online</h1>
          <div className={shared.bannerSubtitle}>
            Schedule your mental health appointment with our qualified providers. Choose between telehealth or in-person visits.
          </div>
        </div>
      </div>
      <div className={shared.landingMain} style={{ marginTop: 0 }}>
        <div className={styles.textParent}>
        <div className={styles.textHeader}>
          <div className={styles.textTitle}>Choose your Appointment Type</div>
          <div className={styles.textSubtitle}>Select how you'd like to meet with your mental health provider</div>
        </div>
        <div className={styles.cards}>
          <div
            className={`${styles.card} ${selected === 'telehealth' ? styles.selected : ''}`}
            onClick={() => setSelected('telehealth')}
          >
            <div className={styles.cardIcon}><img src={telehealthIcon} alt="Telehealth" /></div>
            <div className={styles.cardTitle}>Telehealth</div>
            <div className={styles.cardDesc}>Meet with your provider from the comfort of your home</div>
          </div>
          <div
            className={`${styles.card} ${selected === 'inperson' ? styles.selected : ''}`}
            onClick={() => setSelected('inperson')}
          >
            <div className={styles.cardIcon}><img src={inpersonIcon} alt="In-person" /></div>
            <div className={styles.cardTitle}>In-person</div>
            <div className={styles.cardDesc}>Visit our office for face-to-face consultation</div>
          </div>
        </div>
        <button
          className={`${styles.button} ${selected ? styles.enabled : ''}`}
          disabled={!selected}
          onClick={() => selected && onSelect(selected)}
        >
          Proceed to Provider Selection
        </button>
          <button style={{marginTop: 12}} onClick={onBack}>Back</button>
        </div>
      </div>
      <footer className={shared.landingFooter}>
        <div className={shared.footerContent}>
          <div className={shared.footerTitle}>{axisBranding.organizationName}</div>
          <div className={shared.footerRating}>
            <span className={shared.footerStars}>★★★★★</span>
            <span className={shared.footerAvg}>Average Rating: 4.9/5</span>
          </div>
        </div>
      </footer>
    </div>
  );
});

export default AppointmentTypeStep; 